import { Checkbox, Grid, Menu, MenuItem, TextField } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonWithLoader } from 'components/reusable/button-loader';
import { List } from 'components/reusable/list';
import { ReusableSelect as Select } from 'components/reusable/select';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { changeAccountPermissions, getAccountUsers, linkUserInvite, loadUserInvites, patchUser, postUserInvite, removeUserInvite, resendUserInvite } from 'store/sagas/sagas';
import { validateEmail } from 'utils/email';
import './company-users.css';
import { humanDate } from 'utils/ui.utils';
import { useIsMobile } from 'utils/window';
export const CompanyUsers = () => {
    const { state, dispatch, asyncDispatch } = useGlobalState();
    const [isLoading, setIsLoading] = useState(false);
    const [isInviteLoading, setIsInviteLoading] = useState(false);
    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [users, setUsers] = useState(null);
    const [invites, setInvites] = useState(null);
    const [anchor, setAnchor] = useState({ el: null, id: null });
    const [resendIds, setResendIds] = useState([]);
    const [copyIds, setCopyIds] = useState([]);
    const emailRef = useRef(null);
    const lastNameRef = useRef(null);
    const firstNameRef = useRef(null);
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isAccountAdmin, setIsAccountAdmin] = useState(false);
    const [showDisabled, setShowDisabled] = useState(true);
    const [inviteAccountGroupId, setInviteAccountGroupId] = useState(0);
    const [is2FA, setIs2FA] = useState(false);
    const [isReviewer, setIsReviewer] = useState(false);
    const [isDownloader, setIsDownloader] = useState(false);
    const [invalidData, setInvalidData] = useState(false);
    const handleClose = useCallback(() => {
        setTimeout(() => {
            setAnchor({ el: null, id: null });
        }, 0);
    }, []);
    const handleClick = useCallback((event, id) => {
        setAnchor({ el: event.currentTarget, id });
    }, []);
    const removeInvite = (id) => {
        if (!invites) {
            return;
        }
        setInvites(invites.filter((ent) => ent.id !== id));
    };
    const updateInvite = (invite) => {
        if (!invites) {
            return;
        }
        const newInvites = [...invites];
        const idx = invites.findIndex((j) => j.id === invite.id);
        if (idx !== -1) {
            newInvites[idx] = invite;
            setInvites(newInvites);
        }
    };
    useEffect(() => {
        if (!state.account?.name) {
            return;
        }
        if (!users) {
            setIsLoading(true);
            asyncDispatch(getAccountUsers())
                .then((res) => {
                const filteredUsers = showDisabled
                    ? res.users
                    : res.users.filter((u) => !u.isDisabled);
                setUsers(filteredUsers);
                setIsLoading(false);
            })
                .catch(() => {
                setIsLoading(false);
            });
        }
        if (!invites) {
            setIsInviteLoading(true);
            asyncDispatch(loadUserInvites())
                .then((res) => {
                setInvites(res.users);
                setIsInviteLoading(false);
            })
                .catch(() => {
                setIsInviteLoading(false);
            });
        }
    }, [state.account, showDisabled]);
    const handleChangeFilters = (event) => {
        const value = event.target.value;
        if (value === 'Show') {
            setShowDisabled(true);
            asyncDispatch(getAccountUsers())
                .then((res) => {
                setUsers(res.users); // Set users to the original value of res.users
            })
                .catch((error) => {
                console.error(error);
            });
        }
        else {
            setShowDisabled(false);
            if (!users) {
                return;
            }
            const filteredUsers = users.filter((u) => !u.isDisabled);
            setUsers(filteredUsers);
        }
    };
    const clearForm = () => {
        try {
            if (lastNameRef.current) {
                lastNameRef.current.value = '';
            }
            if (firstNameRef.current) {
                firstNameRef.current.value = '';
            }
            if (emailRef.current) {
                emailRef.current.value = '';
            }
            setEmail('');
            setLastName('');
            setFirstName('');
            setIsReviewer(false);
            setIsDownloader(false);
            setIsAccountAdmin(false);
            setIs2FA(false);
            setInviteAccountGroupId(0);
        }
        catch (error) {
            console.error(error);
        }
    };
    const handleEmail = useCallback((e) => {
        setEmail(e.target.value.trim());
        setInvalidData(false);
    }, []);
    const handleLastName = useCallback((e) => {
        setLastName(e.target.value.trim());
        setInvalidData(false);
    }, []);
    const handleFirstName = useCallback((e) => {
        setFirstName(e.target.value.trim());
        setInvalidData(false);
    }, []);
    const handleAccountGroup = useCallback((e) => {
        setInviteAccountGroupId(Number(e.target.value));
        setInvalidData(false);
    }, []);
    const changePermissions = (currentUser, { isAccountAdmin: newIsAccountAdmin, isReviewer: newIsReviewer, isDownloader: newIsDownloader, isDisabled, is2FA: newIs2FA }) => {
        if (currentUser.id) {
            const permissions = {
                isAccountAdmin: newIsAccountAdmin ?? currentUser.isAccountAdmin,
                isDisabled: isDisabled ?? currentUser.isDisabled,
                isReviewer: newIsReviewer ?? currentUser.isReviewer,
                isDownloader: newIsDownloader ?? currentUser.isDownloader,
                is2FA: newIs2FA ?? currentUser.is2FA
            };
            asyncDispatch(changeAccountPermissions({ id: currentUser.id, permissions }))
                .then((res) => {
                if (users?.length) {
                    const idx = users.findIndex((u) => u.id === res.user.id);
                    users[idx] = res.user;
                    setUsers([...users]);
                }
            })
                .catch(console.error);
        }
    };
    const onLinkUserAccountGroup = useCallback(async (targetAccountGroupId, userId) => {
        const currentUser = users?.find((u) => u.id === userId);
        const previousAccountGroupId = currentUser?.groupId ?? '';
        if (targetAccountGroupId === previousAccountGroupId) {
            return;
        }
        try {
            await asyncDispatch(patchUser({
                userId,
                groupId: targetAccountGroupId
                    ? Number.parseInt(targetAccountGroupId, 10)
                    : null
            }));
            const res = await asyncDispatch(getAccountUsers());
            setUsers(showDisabled ? res.users : res.users.filter((u) => !u.isDisabled));
        }
        catch (error) {
            console.error(error);
            dispatch(setMessages({
                value: 'Failed to update user group',
                severity: 'error'
            }));
        }
    }, [users, showDisabled, dispatch]);
    const onInviteUser = () => {
        if (!validateEmail(email)) {
            setInvalidData(true);
            return;
        }
        setIsInviteLoading(true);
        asyncDispatch(postUserInvite({
            email,
            isReviewer,
            accountGroupId: inviteAccountGroupId || null,
            isDownloader,
            isAccountAdmin,
            is2FA,
            lastName,
            firstName
        }))
            .then(({ user: newUser }) => {
            setInvites([newUser, ...(invites ?? [])]);
            clearForm();
            setIsInviteLoading(false);
        })
            .catch(() => {
            setIsInviteLoading(false);
        });
    };
    const isMobile = useIsMobile();
    return (React.createElement(React.Fragment, null,
        React.createElement("h1", { className: "g-page-name" }, "Users"),
        React.createElement("div", { className: "filters-container-container" },
            React.createElement("div", { className: "filters-container" },
                React.createElement(Grid, { container: true },
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 6 },
                            React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", error: invalidData, inputRef: emailRef, label: "Email", onChange: handleEmail, required: true }))),
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 3 },
                            React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", error: invalidData, inputRef: firstNameRef, label: "First name", onChange: handleFirstName, required: true })),
                        React.createElement(Grid, { item: true, xs: 3 },
                            React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", error: invalidData, inputRef: lastNameRef, label: "Last name", onChange: handleLastName, required: true }))),
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 2 },
                            React.createElement("div", { className: "form-checkbox" }, "Review")),
                        React.createElement(Grid, { item: true, xs: 1 },
                            React.createElement(Checkbox, { checked: isReviewer, className: "form-checkbox", onChange: () => {
                                    setIsReviewer(!isReviewer);
                                } }))),
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 2 },
                            React.createElement("div", { className: "form-checkbox" }, "Files")),
                        React.createElement(Grid, { item: true, xs: 1 },
                            React.createElement(Checkbox, { checked: isDownloader, className: "form-checkbox", onChange: () => {
                                    setIsDownloader(!isDownloader);
                                } }))),
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 2 },
                            React.createElement("div", { className: "form-checkbox" }, "Admin")),
                        React.createElement(Grid, { item: true, xs: 1 },
                            React.createElement(Checkbox, { checked: isAccountAdmin, className: "form-checkbox", disabled: !!inviteAccountGroupId, onChange: () => {
                                    setIsAccountAdmin(!isAccountAdmin);
                                } }))),
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 2 },
                            React.createElement("div", { className: "form-checkbox" }, "2FA")),
                        React.createElement(Grid, { item: true, xs: 1 },
                            React.createElement(Checkbox, { checked: is2FA, className: "form-checkbox", onChange: () => {
                                    setIs2FA(!is2FA);
                                } }))),
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 2 },
                            React.createElement("div", { className: "form-checkbox" }, "Group")),
                        React.createElement(Grid, { item: true, xs: 3 }, isAccountAdmin ? null : (React.createElement(Select, { className: "select-group", items: (state.accountGroups ?? []).map((accGrp) => ({
                                id: `${accGrp.id}`,
                                name: accGrp.name
                            })), name: "pageSize", onChange: handleAccountGroup, value: inviteAccountGroupId === 0
                                ? ''
                                : `${inviteAccountGroupId}` }))))),
                React.createElement(Grid, { container: true },
                    React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                        React.createElement(Grid, { item: true, xs: 3 },
                            React.createElement(ButtonWithLoader, { className: "u-margin-top-large", disabled: !email || !firstName || !lastName, isLoading: isLoading, name: "Invite User", onClick: onInviteUser })))))),
        React.createElement("div", { className: "filters-container-container" },
            React.createElement("div", { className: "filters__container" },
                React.createElement("div", { className: "filters", onClick: () => {
                        setIsOpenFilters(!isOpenFilters);
                    } },
                    React.createElement(FilterListIcon, { style: {
                            color: isOpenFilters ? '#4122af' : 'grey'
                        } }),
                    React.createElement("span", null, "Filter")))),
        isOpenFilters && (React.createElement("div", { className: "filters-container" },
            React.createElement(Grid, { className: "u-margin-top-medium", container: true },
                React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                    React.createElement(Grid, { item: true, xs: isMobile ? 12 : 3 },
                        React.createElement(Select, { className: `g-full-width ${isMobile ? 'u-margin-bottom-large' : ''}`, isClearable: false, itemTestId: "menu-disabled", items: [
                                { id: 'Show', name: 'Show' },
                                { id: 'Hide', name: 'Hide' }
                            ], label: "Disabled", name: "disabled", onChange: handleChangeFilters, testId: "select-disabled", value: showDisabled ? 'Show' : 'Hide' })))))),
        !!invites?.length && (React.createElement(List, { columns: [
                {
                    name: 'Name',
                    xs: 2,
                    customRenderValue: ({ firstName: displayFirstName, lastName: displayLastName }) => {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: "text-ellipsis" }, displayFirstName),
                            React.createElement("div", { className: "text-ellipsis" }, displayLastName)));
                    }
                },
                {
                    name: 'Email',
                    xs: 2,
                    id: 'email'
                },
                {
                    name: 'Review',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.isReviewer, disabled: true, onChange: () => {
                                changePermissions(item, {
                                    isReviewer: !item.isReviewer
                                });
                            } }));
                    }
                },
                {
                    name: 'Files',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.isDownloader, disabled: true, onChange: () => {
                                changePermissions(item, {
                                    isDownloader: !item.isDownloader
                                });
                            } }));
                    }
                },
                {
                    name: 'Admin',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.isAccountAdmin, disabled: true, onChange: () => {
                                changePermissions(item, {
                                    isAccountAdmin: !item.isAccountAdmin
                                });
                            } }));
                    }
                },
                {
                    name: '2FA',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.is2FA, disabled: true, onChange: () => {
                                changePermissions(item, {
                                    is2FA: !item.is2FA
                                });
                            } }));
                    }
                },
                {
                    name: 'Days Left',
                    xs: 1,
                    customRenderValue: (item) => {
                        const expire = new Date(item.inviteExpiredAt);
                        const now = new Date();
                        const diff = Math.ceil((expire.getTime() - now.getTime()) / 1000 / 60 / 60 / 24);
                        return diff > 0 ? `${diff}` : 'Expired';
                    }
                },
                {
                    xs: 1,
                    name: '',
                    isClickable: false,
                    customRenderValue: ({ id }) => {
                        return (React.createElement("div", { className: "pointer", onClick: (e) => {
                                handleClick(e, id);
                            } },
                            React.createElement("i", { className: "candidates__menu_icon fas fa-ellipsis-v" }),
                            React.createElement(Menu, { anchorEl: anchor.el, id: String(id), keepMounted: true, onClose: handleClose, open: anchor.id === id },
                                React.createElement(MenuItem, { onClick: () => {
                                        setResendIds([...resendIds, id]);
                                        asyncDispatch(resendUserInvite(id))
                                            .then(({ user: newUser }) => {
                                            updateInvite(newUser);
                                            setTimeout(() => {
                                                handleClose();
                                            }, 500);
                                        })
                                            .catch(console.error);
                                    } }, resendIds.includes(id) ? 'Resent' : 'Resend'),
                                React.createElement(MenuItem, { onClick: () => {
                                        setCopyIds([...copyIds, id]);
                                        asyncDispatch(linkUserInvite(id))
                                            .then((data) => {
                                            navigator.clipboard
                                                .writeText(data.url)
                                                .catch(console.error);
                                            setTimeout(() => {
                                                handleClose();
                                            }, 500);
                                        })
                                            .catch(console.error);
                                    } }, copyIds.includes(id) ? 'Invite copied' : 'Copy invite'),
                                React.createElement(MenuItem, { onClick: () => {
                                        asyncDispatch(removeUserInvite(id))
                                            .then(() => {
                                            removeInvite(id);
                                            setTimeout(() => {
                                                handleClose();
                                            }, 500);
                                        })
                                            .catch(console.error);
                                    } }, "Remove"))));
                    }
                }
            ], items: invites, label: "invites", listSource: "users", loading: isInviteLoading, total: invites.length })),
        React.createElement(List, { columns: [
                {
                    name: 'Name',
                    xs: 1,
                    customRenderValue: ({ firstName: displayFirstName, lastName: displayLastName }) => {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: "text-ellipsis" }, displayFirstName),
                            React.createElement("div", { className: "text-ellipsis" }, displayLastName)));
                    }
                },
                {
                    name: 'Email',
                    xs: 2,
                    id: 'email'
                },
                {
                    name: 'Last Login',
                    xs: 2,
                    customRenderValue: ({ loginAt }) => {
                        return loginAt ? (React.createElement("div", { className: "text-ellipsis" }, humanDate(loginAt))) : null;
                    }
                },
                {
                    name: 'Review',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.isReviewer, onChange: () => {
                                changePermissions(item, {
                                    isReviewer: !item.isReviewer
                                });
                            } }));
                    }
                },
                {
                    name: 'Files',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.isDownloader, onChange: () => {
                                changePermissions(item, {
                                    isDownloader: !item.isDownloader
                                });
                            } }));
                    }
                },
                {
                    name: 'Admin',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.isAccountAdmin, disabled: !!item.groupId, onChange: () => {
                                changePermissions(item, {
                                    isAccountAdmin: !item.isAccountAdmin
                                });
                            } }));
                    }
                },
                {
                    name: '2FA',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.is2FA, onChange: () => {
                                changePermissions(item, {
                                    is2FA: !item.is2FA
                                });
                            } }));
                    }
                },
                {
                    name: 'Disabled',
                    xs: 1,
                    customRenderValue: (item) => {
                        return (React.createElement(Checkbox, { checked: item.isDisabled, onChange: () => {
                                changePermissions(item, {
                                    isDisabled: !item.isDisabled
                                });
                            } }));
                    }
                },
                {
                    name: 'Group',
                    xs: 2,
                    customRenderValue: ({ id, groupId }) => {
                        const groups = (state.accountGroups ?? []).map((accGrp) => ({
                            id: `${accGrp.id}`,
                            name: accGrp.name
                        }));
                        return (React.createElement(Select, { className: "select-group", items: groups, name: "pageSize", 
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onChange: async (e) => onLinkUserAccountGroup(e.target.value, id), value: groupId ?? '' }));
                    }
                }
            ], items: users, label: "users", listSource: "users", loading: isLoading, total: users ? users.length : 0 })));
};
